// -----------------------------------------------------------------------------
// Incidents repository — reads from Supabase public.incidents and normalises
// rows into the shape the existing UI already expects. Kept separate from the
// UI so the data source can change without touching components.
//
// Actual table schema: id (int), sensor1, sensor2, sensor3 (numeric),
// risk_level (text, e.g. "CRITICAL"), created_at (timestamptz, nullable).
// -----------------------------------------------------------------------------
import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";

// Representative severity value per band so the existing risk gauge can render
// a categorical risk_level. This is derived from risk_level, not a stored score.
const LEVEL_SCORE = { SAFE: 8, LOW: 25, MEDIUM: 55, HIGH: 88 };

// Map any incoming risk_level string onto the app's semantic bands.
export function mapRiskLevel(raw) {
  const s = String(raw || "").trim().toUpperCase();
  if (["HIGH", "CRITICAL", "SEVERE", "DANGER", "EMERGENCY"].includes(s)) return "HIGH";
  if (["MEDIUM", "MODERATE", "WARN", "WARNING", "ELEVATED"].includes(s)) return "MEDIUM";
  if (["LOW", "MINOR", "GUARDED"].includes(s)) return "LOW";
  if (["SAFE", "NONE", "OK", "NORMAL", "CLEAR"].includes(s)) return "SAFE";
  return "MEDIUM";
}

const titleCase = (s) =>
  String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// Pull every sensor* column into an ordered list of readings.
function extractSensors(row) {
  return Object.keys(row)
    .filter((k) => /^sensor/i.test(k))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((k, i) => {
      const num = (k.match(/(\d+)/) || [])[1] || i + 1;
      return { sensor: `Sensor ${num}`, key: k, reading: String(row[k]) };
    });
}

export function normalizeIncident(row) {
  const level = mapRiskLevel(row.risk_level);
  const rawLevel = row.risk_level ? String(row.risk_level).toUpperCase() : level;
  return {
    id: `INC-${row.id}`,
    rawId: row.id,
    level,
    rawLevel,
    score: LEVEL_SCORE[level],
    scoreIsDerived: true,
    eventType: `${titleCase(rawLevel)} risk detected`,
    timestamp: row.created_at || null,
    status: rawLevel,
    locationAvailable: false,
    location: null,
    sensors: extractSensors(row),
    aiContext: null,
    recommendedAction: null,
    verification: level === "HIGH" ? "Awaiting user response" : "—",
    escalation: level === "HIGH" ? "Active" : "Not triggered",
    source: "supabase",
  };
}

// Fetch newest-first, keeping rows with a real timestamp ahead of null ones.
export async function fetchIncidents(limit = 50) {
  if (!isSupabaseConfigured || !supabase) return null;
  const { data, error } = await supabase
    .from("incidents")
    .select("*")
    .order("created_at", { ascending: false, nullsFirst: false })
    .order("id", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(normalizeIncident);
}

// Realtime subscription — invokes onChange on any insert/update/delete.
export function subscribeIncidents(onChange) {
  if (!isSupabaseConfigured || !supabase) return () => {};
  const channel = supabase
    .channel("public:incidents")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "incidents" },
      () => onChange()
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}
